import React from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

interface EventRegistrationFormProps {
  formName?: string;
  className?: string;
}

/**
 * Netlify-backed event registration form with honeypot protection.
 * Multilingual labels (it, es, en). Submits natively to Netlify.
 */
export const EventRegistrationForm: React.FC<EventRegistrationFormProps> = ({
  formName = 'event-registration-la-habana',
  className = ''
}) => {
  const { language } = useLanguage();

  const labels = {
    it: {
      title: 'Registrazione Conferenza',
      subtitle: 'Compila il modulo per riservare il tuo posto all’evento.',
      name: 'Nome e Cognome',
      email: 'Email',
      phone: 'Telefono (opzionale)',
      country: 'Paese di Residenza',
      seats: 'Numero di posti',
      notes: 'Note (opzionale)',
      submit: 'Prenota il tuo posto',
      sending: 'Invio in corso...'
    },
    es: {
      title: 'Registro a la Conferencia',
      subtitle: 'Complete el formulario para reservar su lugar en el evento.',
      name: 'Nombre y Apellido',
      email: 'Correo electrónico',
      phone: 'Teléfono (opcional)',
      country: 'País de Residencia',
      seats: 'Número de puestos',
      notes: 'Notas (opcional)',
      submit: 'Reserva tu lugar',
      sending: 'Enviando...'
    },
    en: {
      title: 'Conference Registration',
      subtitle: 'Fill out the form to reserve your seat at the event.',
      name: 'Full Name',
      email: 'Email',
      phone: 'Phone (optional)',
      country: 'Country of Residence',
      seats: 'Number of seats',
      notes: 'Notes (optional)',
      submit: 'Reserve your seat',
      sending: 'Sending...'
    }
  } as const;

  const t = labels[language];

  return (
    <section className={`mt-12 border-t pt-8 ${className}`}>
      <div className="bg-blue-50 rounded-xl p-6 md:p-8">
        <h3 className="text-2xl font-bold text-blue-900 mb-2">{t.title}</h3>
        <p className="text-blue-800 mb-6">{t.subtitle}</p>

        <form
          name={formName}
          method="POST"
          data-netlify="true"
          data-netlify-honeypot="bot-field"
          className="space-y-6"
          onSubmit={(e) => {
            const form = e.target as HTMLFormElement;
            const formData = new FormData(form);

            const name = String(formData.get('name') || '').trim();
            const email = String(formData.get('email') || '').trim();
            const seats = Number(formData.get('seats') || 0);
            const country = String(formData.get('country') || '').trim();

            if (!name || !email || !/\S+@\S+\.\S+/.test(email) || !country || seats < 1) {
              e.preventDefault();
              alert('Per favore, compila tutti i campi richiesti correttamente.');
              return;
            }

            const submitBtn = form.querySelector('button[type="submit"]') as HTMLButtonElement | null;
            if (submitBtn) {
              submitBtn.disabled = true;
              submitBtn.textContent = t.sending;
            }
          }}
        >
          {/* Netlify hidden form-name field */}
          <input type="hidden" name="form-name" value={formName} />

          {/* Honeypot field */}
          <div style={{ display: 'none' }}>
            <label>
              Don’t fill this out if you’re human:
              <input name="bot-field" />
            </label>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-blue-900 mb-2">
                {t.name} *
              </label>
              <Input id="name" name="name" placeholder="Mario Rossi" required />
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-blue-900 mb-2">
                {t.email} *
              </label>
              <Input id="email" name="email" type="email" placeholder="email@example.com" required />
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-blue-900 mb-2">
                {t.phone}
              </label>
              <Input id="phone" name="phone" type="tel" placeholder="+39 123 456 7890" />
            </div>
            <div>
              <label htmlFor="country" className="block text-sm font-medium text-blue-900 mb-2">
                {t.country} *
              </label>
              <Input id="country" name="country" placeholder="Italia" required />
            </div>
            <div>
              <label htmlFor="seats" className="block text-sm font-medium text-blue-900 mb-2">
                {t.seats} *
              </label>
              <Input id="seats" name="seats" type="number" min={1} placeholder="1" required />
            </div>
          </div>

          <div>
            <label htmlFor="notes" className="block text-sm font-medium text-blue-900 mb-2">
              {t.notes}
            </label>
            <Textarea id="notes" name="notes" rows={4} placeholder="Scrivi eventuali note..." />
          </div>

          <Button type="submit" className="bg-blue-700 hover:bg-blue-800 text-white">
            {t.submit}
          </Button>
        </form>
      </div>
    </section>
  );
};

export default EventRegistrationForm;